import { useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { formatCurrency } from "@/utils/formatCurrency";
import { ProfitTrendsData, buildTrendCards, toPct, PeriodTab, ValueMode, TrendCardData } from "@trader-dashboard/shared";
import { motion, AnimatePresence } from "framer-motion";

type TrendMetricCardsProps = {
  profitTrends: ProfitTrendsData;
  totalDeposits: number;
  period: PeriodTab;
  valueMode: ValueMode;
};

const profitColor = (v: number) => (v >= 0 ? "text-green-600" : "text-red-600");
const fmtCur = (v: number) => formatCurrency(v, { showSign: v > 0 });
const fmtPct = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;

const fmtVal = (dollar: number, mode: ValueMode, deposits: number): string =>
  mode === "$" ? fmtCur(dollar) : fmtPct(toPct(dollar, deposits));

const fmtCardValue = (card: TrendCardData, mode: ValueMode, deposits: number): string => {
  if (card.forcePercent) {
    return fmtPct(toPct(card.value, card.percentBase ?? deposits));
  }
  return fmtVal(card.value, mode, deposits);
};

const fmtSubtitle = (card: TrendCardData, mode: ValueMode, deposits: number): string => {
  if (card.tradeCount !== undefined) {
    return `${card.tradeCount} ${card.tradeCount === 1 ? 'trade' : 'trades'}`;
  }
  if (card.subtitleTemplate && card.subtitleValue !== undefined) {
    return card.subtitleTemplate.replace('{v}', fmtVal(card.subtitleValue, mode, deposits));
  }
  return card.subtitleText ?? '';
};

const MetricCard = ({ card, mode, deposits }: { card: TrendCardData; mode: ValueMode; deposits: number }) => {
  const value = fmtCardValue(card, mode, deposits);
  const displayValue = card.forcePercent ? toPct(card.value, card.percentBase ?? deposits) : card.value;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{card.title}</CardDescription>
        <CardTitle className="flex items-baseline gap-2">
          <span className={`text-3xl ${profitColor(displayValue)}`}>{value}</span>
          {card.valueSuffix && <span className="text-sm text-muted-foreground font-normal">{card.valueSuffix}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{fmtSubtitle(card, mode, deposits)}</p>
      </CardContent>
    </Card>
  );
};

export const TrendMetricCards = ({ profitTrends, totalDeposits, period, valueMode }: TrendMetricCardsProps): React.ReactNode => {
  const cards = useMemo(
    () => buildTrendCards(profitTrends, period, totalDeposits),
    [profitTrends, period, totalDeposits]
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${period}-${valueMode}`}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        {cards.map((card) => (
          <MetricCard key={card.title} card={card} mode={valueMode} deposits={totalDeposits} />
        ))}
      </motion.div>
    </AnimatePresence>
  );
};
